import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useOverlayStack } from "@widget/popups/overlay-stack";
const MobileLayerContext = createContext(null);
// True while the footer shows a Close bar for the top layer
const MobileCloseBarContext = createContext(false);
let nextLayerId = 1;
/**
 * Holds the open page layers for the mobile shell. The last opened one is on top:
 * its title shows in the header and the footer Close bar closes it.
 */
export const useMobileLayerStack = () => {
    var _a, _b;
    const [layers, setLayers] = useState([]);
    const set = useCallback((layer) => setLayers(prev => {
        const i = prev.findIndex(l => l.id === layer.id);
        if (i < 0)
            return [...prev, layer];
        const next = [...prev];
        next[i] = layer;
        return next;
    }), []);
    const remove = useCallback((id) => setLayers(prev => prev.filter(l => l.id !== id)), []);
    const value = useMemo(() => ({ set, remove }), [set, remove]);
    // A layer may give only a title (e.g. a details panel inside a page that owns the close)
    const top = layers.length ? {
        title: (_a = [...layers].reverse().find(l => l.title)) === null || _a === void 0 ? void 0 : _a.title,
        onClose: (_b = [...layers].reverse().find(l => l.onClose)) === null || _b === void 0 ? void 0 : _b.onClose,
    } : undefined;
    return { top, value };
};
export const MobileLayerProvider = ({ value, hasCloseBar = false, children }) => _jsx(MobileLayerContext.Provider, { value: value, children: _jsx(MobileCloseBarContext.Provider, { value: hasCloseBar, children: children }) });
/**
 * Registers a full-screen layer on the mobile shell while `active` is true:
 * the header shows `title` and the footer shows a Close bar that calls `onClose`.
 * Either may be left out: the newest layer with a title gives the header, the newest with
 * `onClose` gives the Close bar.
 * Does nothing outside the mobile shell (desktop, embedded pages).
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false);
 * useMobileLayer(open, { title: 'Order 1042', onClose: () => setOpen(false) });
 * ```
 * @export
 */
const useMobileLayer = (active, options) => {
    const context = useContext(MobileLayerContext);
    const idRef = useRef(0);
    if (!idRef.current)
        idRef.current = nextLayerId++;
    // The Close bar always calls the latest onClose
    const onCloseRef = useRef(options.onClose);
    onCloseRef.current = options.onClose;
    const closable = !!options.onClose;
    useEffect(() => {
        if (!active || !context)
            return;
        context.set({ id: idRef.current, title: options.title, onClose: closable ? () => { var _a; return (_a = onCloseRef.current) === null || _a === void 0 ? void 0 : _a.call(onCloseRef); } : undefined });
    }, [active, context, options.title, closable]);
    useEffect(() => {
        if (!active || !context)
            return;
        const id = idRef.current;
        return () => context.remove(id);
    }, [active, context]);
    // Back on phones closes the layer, like its Close bar (overlay-stack)
    useOverlayStack(active && closable, () => { var _a; return (_a = onCloseRef.current) === null || _a === void 0 ? void 0 : _a.call(onCloseRef); });
};
/**
 * True inside the mobile shell, where `useMobileLayer` gives the header title and the Close bar.
 * Outside it (standalone pages) show your own back or close control.
 * @export
 */
export const useHasMobileShell = () => !!useContext(MobileLayerContext);
/**
 * True while the mobile shell's footer shows a Close bar (an open layer can be closed from it).
 * Leave out your own close button then: the Close bar and the phone's Back already close it.
 * @export
 */
export const useHasMobileCloseBar = () => useContext(MobileCloseBarContext);
export default useMobileLayer;
